/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useEffect } from 'react';
import { gnoment } from 'shared-ui/gnoment';
import { useNavigate } from 'react-router';

import { Alert, Button, FlexCol, FlexRow } from 'sarsaparilla';
import type {
    TreePermitData,
    AvailabilityEntry,
    PermitType,
} from '../../../types/permit';
import { formatOnSaleTime, getFacilityTimeZone } from '../../utils/timeZone';

interface CTABarTypes {
    treePermitId: string;
    treePermit: TreePermitData;
    permitTypes: Array<PermitType>;
    availability: Record<string, Partial<AvailabilityEntry>>;
}

export default function CTABar({
    treePermitId,
    treePermit,
    permitTypes,
    availability,
}: CTABarTypes) {
    const navigate = useNavigate();
    const hasPermitTypes = !!permitTypes.length;
    const [openSeason, setOpenSeason] = useState(gnoment());
    useEffect(() => {
        const { on_sale } = treePermit;
        if (on_sale) {
            setOpenSeason(gnoment(on_sale));
        }
    }, [treePermit]);
    const seasonIsOpen = gnoment().isSameOrAfter(openSeason);
    const date = treePermit?.on_sale
        ? formatOnSaleTime(treePermit.on_sale, getFacilityTimeZone(treePermit))
        : '';
    const handlePurchaseButton = () => {
        if (seasonIsOpen) {
            navigate(`/tree-permits/${treePermitId}/order`);
        }
    };

    const havePermits = permitTypes.length > 0;

    const haveAvailable = permitTypes
        .map((item) => availability[item.id])
        .find(
            (value) =>
                value?.availability?.unlimited ||
                (value?.availability?.available && value?.availability?.available > 0)
        );

    return (
        <div className="tp-cta-bar">
            <FlexRow>
                <FlexCol>
                    <div className="tp-cta-bar-container">
                        <Button
                            onClick={handlePurchaseButton}
                            isDisabled={!havePermits || !seasonIsOpen || !haveAvailable}
                            className="tp-cta-bar-button"
                            gaTrackingId="240409238128"
                        >
                            Purchase Tree Permit
                        </Button>
                        {!seasonIsOpen && hasPermitTypes && (
                            <Alert hasBoxStyling={false} className="tp-cta-bar-alert">
                                Christmas Tree Permits will go on sale {date}.
                            </Alert>
                        )}
                        {seasonIsOpen && hasPermitTypes && !haveAvailable && (
                            <Alert hasBoxStyling={false} className="tp-cta-bar-alert">
                                Christmas Tree Permits for this season are currently sold
                                out.
                            </Alert>
                        )}
                    </div>
                </FlexCol>
            </FlexRow>
        </div>
    );
}
